// src/context/AuthContext.jsx
// Auth state — now wired to real backend via api/client.js

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth as authApi } from '../api/client';

const AuthContext = createContext(null);
const getErrorMessage = (err) => err?.data?.error || err?.message || 'Request failed';

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // Restore session on mount
  useEffect(() => {
    if (!authApi.isLoggedIn()) { setLoading(false); return; }
    authApi.me()
      .then(({ user }) => setUser(user))
      .catch(() => { authApi.logout(); })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async ({ email, password }) => {
    setError(null);
    try {
      const { user } = await authApi.login({ email, password });
      setUser(user);
      return user;
    } catch (err) {
      authApi.logout();
      setUser(null);
      setError(getErrorMessage(err));
      return null;
    }
  }, []);

  const register = useCallback(async ({ email, full_name, password }) => {
    setError(null);
    try {
      const data = await authApi.register({ email, full_name, password });
      if (data?.user) setUser(data.user);
      return data;
    } catch (err) {
      authApi.logout();
      setUser(null);
      setError(getErrorMessage(err));
      return null;
    }
  }, []);

  const verifyEmailCode = useCallback(async ({ email, code }) => {
    setError(null);
    try {
      const data = await authApi.verifyEmailCode({ email, code });
      if (data?.user) setUser(data.user);
      return data;
    } catch (err) {
      authApi.logout();
      setUser(null);
      setError(getErrorMessage(err));
      return null;
    }
  }, []);

  const resendVerificationCode = useCallback(async (email) => {
    setError(null);
    try {
      return await authApi.resendVerificationCode(email);
    } catch (err) {
      setError(getErrorMessage(err));
      return null;
    }
  }, []);

  const logout = useCallback(() => {
    authApi.logout();
    setError(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    if (!authApi.isLoggedIn()) return;
    try {
      const { user } = await authApi.me();
      setUser(user);
      return user;
    } catch (err) {
      authApi.logout();
      setUser(null);
      setError(getErrorMessage(err));
      return null;
    }
  }, []);

  const updateUser = useCallback((updates) => {
    setUser((current) => (current ? { ...current, ...updates } : current));
  }, []);

  const isPremium  = user?.tier === 'researcher' || user?.tier === 'annual' || user?.tier === 'pro' || user?.tier === 'lifetime';
  const isLifetime = user?.tier === 'lifetime';
  const isAuthenticated = !!user;
  const tier       = user?.tier || 'free';

  return (
    <AuthContext.Provider value={{
      user, loading, error, isPremium, isLifetime, isAuthenticated, tier,
      login, register, verifyEmailCode, resendVerificationCode,
      logout, refreshUser, updateUser, setError,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside <AuthProvider>');
  return ctx;
};

// Convenience: authenticated fetch already handled by api/client.js
// This export kept for backward compatibility with older components
export const authFetch = (path, opts = {}) => {
  const token = localStorage.getItem('dd_token');
  return fetch(
    `${import.meta.env.VITE_API_URL || 'https://8zgroi30f1.execute-api.us-east-1.amazonaws.com/dev'}${path}`,
    {
      ...opts,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(opts.headers || {}),
      },
    }
  );
};
